import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import Card from "../../../components/UI/card/Card";
import { Formik, Field, Form, ErrorMessage, FormikHelpers } from "formik";
import * as Yup from "yup";
import { getUserId } from "../../../services/auth.service";
import {
  createNewEmployee,
  updateEmployeeById,
} from "../../../services/employee.service";
import { EMPLOYEES_ROLES, USER_DESIGNATIONS } from "../../../constants";
import { ROUTES } from "../../../constants/routes";
import { getApi } from "../../../services/http.service";
import { IEmployee } from "../../../interfaces/employee.interface";
import VisibilityBox from "../../../components/common/VisibilityBox";
import { STATES_OPTIONS } from "../../../constants/states";

const validationSchema = Yup.object({
  fullName: Yup.string().required("Full name is required"),
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email format"),
  phone: Yup.string()
    .required("Phone number is required")
    .matches(/^\d{10}$/, "Phone number must be 10 digits"),
  password: Yup.string().optional(),
  designation: Yup.string().required("Designation is required"),
  salary: Yup.number()
    .required("Salary is required")
    .positive("Salary must be a positive number"),
  address: Yup.object({
    street: Yup.string().required("Street is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    country: Yup.string().default("India"),
    pincode: Yup.string().required("Pincode is required"),
  }),
  permanentAddress: Yup.object({
    street: Yup.string().required("Street is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    country: Yup.string().default("India"),
    pincode: Yup.string().required("Pincode is required"),
  }),
  role: Yup.string().oneOf(EMPLOYEES_ROLES).required("Role is required"),
  accountNumber: Yup.string().optional(),
  adhaarCard: Yup.string().optional(),
  employeeCode: Yup.string().optional(),
  panCardNumber: Yup.string().optional(),
  isLogged: Yup.boolean().optional(),
  headQuarter: Yup.string().optional(),
  da: Yup.number().default(0),
  ta: Yup.number().default(0),
  reporters: Yup.array().of(Yup.string()).optional(),
});

const EmployeeForm: React.FC<any> = () => {
  const { t } = useTranslation();
  const params = useParams();
  const [displayImage, setDisplayImage] = useState<any>(null);
  const [employee, setEmployee] = useState<IEmployee>();
  const [loading, setLoading] = useState<boolean>(true);
  const [workingCities, setWorkingCities] = useState<string[]>([]);
  const [workingStates, setWorkingStates] = useState<string[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchDistributor();
  }, []);

  const fetchDistributor = async () => {
    try {
      const result = await getApi(ROUTES.EMPLOYEE_BY_ID + params?.empId);
      setEmployee(result?.data);
      setWorkingCities(result?.data?.workingCities || []);
      setWorkingStates(result?.data?.workingStates || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <div className="w-100">
      <div className="row justify-content-center">
        <div className="col-lg-12 col-md-12 col-sm-12 w-100">
          <Card>
            <div className="product__edit p-4 w-100">
              <div className="d-flex">
                <Link className="nav-link" to={"/employees"}>
                  <Icon
                    icon="material-symbols:arrow-back-rounded"
                    fontSize={25}
                  />
                </Link>
                <h4 className="ms-2">Employee</h4>
              </div>
              <div className=" mb-2">
                <label htmlFor="pic" className="upload-icon mb-2">
                  <Icon
                    icon="akar-icons:cloud-upload"
                    style={{ fontSize: "40px", color: "#007bff" }}
                  />
                </label>
                <div className="d-none">
                  <input
                    className="form-control-file"
                    type="file"
                    id="pic"
                    name="pic"
                    onChange={(ev: any) =>
                      setDisplayImage(ev.currentTarget.files[0])
                    }
                    accept="image/png, image/jpeg"
                  />
                </div>
                <img
                  className="img-fluid rounded mt-3"
                  src={
                    displayImage
                      ? URL.createObjectURL(displayImage)
                      : "/local/default.png"
                  }
                  alt="profile pic"
                  style={{ maxWidth: "200px" }}
                />
              </div>

              {/* Formik Form */}
              <Formik
                initialValues={{
                  fullName: employee?.fullName || "",
                  email: employee?.email || "",
                  phone: employee?.phone || "",
                  password: employee?.password || "",
                  designation: employee?.designation || "",
                  salary: employee?.salary || 0,
                  address: employee?.address || {
                    street: "",
                    city: "",
                    state: "",
                    country: "India",
                    pincode: "",
                  },
                  permanentAddress: employee?.permanentAddress || {
                    street: "",
                    city: "",
                    state: "",
                    country: "India",
                    pincode: "",
                  },
                  accountNumber: employee?.accountNumber || "",
                  adhaarCard: employee?.adhaarCard || "",
                  panCardNumber: employee?.panCardNumber || "",
                  isLogged: employee?.isLogged || false,
                  headQuarter: employee?.headQuarter || "",
                  da: employee?.da || 0,
                  ta: employee?.ta || 0,
                  reporters: employee?.reporters || [],
                  role: employee?.role || EMPLOYEES_ROLES[0], // Default to first role
                  targetAchieve: employee?.targetAchieve || 0,
                  employeeCode: employee?.employeeCode || "",
                }}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={async (
                  values,
                  { resetForm }: FormikHelpers<IEmployee>
                ) => {
                  if (params?.empId) {
                    const payload = {
                      ...values,
                      workingCities,
                      workingStates,
                      createdBy: getUserId(),
                    };
                    const result = await updateEmployeeById(
                      params?.empId,
                      payload
                    );
                    if (result) {
                      resetForm();
                      setDisplayImage(null);
                      navigate("/employees");
                    }
                  } else {
                    const payload = {
                      ...values,
                      createdBy: getUserId(),
                    };
                    const result = await createNewEmployee(payload);
                    if (result) {
                      resetForm();
                      setDisplayImage(null);
                      navigate("/employees");
                    }
                  }
                }}
              >
                {({ errors, touched, values, setFieldValue }) => (
                  <Form>
                    {/* Full Name */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="fullName"
                        name="fullName"
                        type="text"
                        placeholder="Full Name"
                        className={`form-control ${
                          errors.fullName && touched.fullName
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="fullName"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Email */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Email"
                        className={`form-control ${
                          errors.email && touched.email ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Phone */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="phone"
                        name="phone"
                        type="text"
                        placeholder="Phone Number"
                        className={`form-control ${
                          errors.phone && touched.phone ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="phone"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Password */}
                    <VisibilityBox show={params?.empId !== undefined}>
                      <div className="form-group p-0 mb-3">
                        <Field
                          id="password"
                          name="password"
                          type="password"
                          placeholder="Password"
                          className={`form-control ${
                            errors.password && touched.password
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <ErrorMessage
                          name="password"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </VisibilityBox>
                    {/* Designation */}
                    <div className="form-group p-0 mb-3">
                      <Field as="select" name="designation" className="form-control">
                        {USER_DESIGNATIONS.map((role, index) => (
                          <option key={index} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="designation"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Salary */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="salary"
                        name="salary"
                        type="number"
                        placeholder="Salary"
                        className={`form-control ${
                          errors.salary && touched.salary ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="salary"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Target Achieve */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="targetAchieve"
                        name="targetAchieve"
                        type="number"
                        placeholder="Target Achieve"
                        className={`form-control ${
                          errors.targetAchieve && touched.targetAchieve
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="targetAchieve"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Address Fields */}
                    <h5>Address</h5>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="address.street"
                        name="address.street"
                        type="text"
                        placeholder="Street"
                        className={`form-control ${
                          errors.address?.street && touched.address?.street
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="address.street"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="address.city"
                        name="address.city"
                        type="text"
                        placeholder="City"
                        className={`form-control ${
                          errors.address?.city && touched.address?.city
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="address.city"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="address.state"
                        name="address.state"
                        type="text"
                        placeholder="State"
                        className={`form-control ${
                          errors.address?.state && touched.address?.state
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="address.state"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="address.pincode"
                        name="address.pincode"
                        type="text"
                        placeholder="Pincode"
                        className={`form-control ${
                          errors.address?.pincode && touched.address?.pincode
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="address.pincode"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Permanent Address Fields */}
                    <h5>Permanent Address</h5>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="permanentAddress.street"
                        name="permanentAddress.street"
                        type="text"
                        placeholder="Street"
                        className={`form-control ${
                          errors.permanentAddress?.street &&
                          touched.permanentAddress?.street
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="permanentAddress.street"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="permanentAddress.city"
                        name="permanentAddress.city"
                        type="text"
                        placeholder="City"
                        className={`form-control ${
                          errors.permanentAddress?.city &&
                          touched.permanentAddress?.city
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="permanentAddress.city"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="permanentAddress.state"
                        name="permanentAddress.state"
                        type="text"
                        placeholder="State"
                        className={`form-control ${
                          errors.permanentAddress?.state &&
                          touched.permanentAddress?.state
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="permanentAddress.state"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="permanentAddress.pincode"
                        name="permanentAddress.pincode"
                        type="text"
                        placeholder="Pincode"
                        className={`form-control ${
                          errors.permanentAddress?.pincode &&
                          touched.permanentAddress?.pincode
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="permanentAddress.pincode"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Account Number */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="accountNumber"
                        name="accountNumber"
                        type="text"
                        placeholder="Account Number"
                        className={`form-control ${
                          errors.accountNumber && touched.accountNumber
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="accountNumber"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Aadhar Card */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="adhaarCard"
                        name="adhaarCard"
                        type="text"
                        placeholder="Aadhar Card"
                        className={`form-control ${
                          errors.adhaarCard && touched.adhaarCard
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="adhaarCard"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Pan Card */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="panCardNumber"
                        name="panCardNumber"
                        type="text"
                        placeholder="Pan Card Number"
                        className={`form-control ${
                          errors.panCardNumber && touched.panCardNumber
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="panCardNumber"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Role */}
                    <div className="form-group p-0 mb-3">
                      <Field as="select" name="role" className="form-control">
                        {EMPLOYEES_ROLES.map((role, index) => (
                          <option key={index} value={role}>
                            {role}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="role"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="form-group p-0 mb-3">
                      <Field
                        id="employeeCode"
                        name="employeeCode"
                        type="text"
                        placeholder="Employee Code"
                        className={`form-control ${
                          errors.employeeCode && touched.employeeCode
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="employeeCode"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="w-100 mb-2">
                      <div className="d-flex flex-wrap gap-2 px-1">
                        {workingCities.map((d: any, dIx: number) => (
                          <span key={dIx} className="badge bg-primary p-2">
                            {d}
                            <Icon
                              icon="material-symbols:close-rounded"
                              className="cursor-pointer ms-1"
                              onClick={() => {
                                const updated = workingCities.filter(
                                  (_, i) => i !== dIx
                                );
                                setWorkingCities(updated);
                              }}
                            />
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="w-100 mb-3">
                      <div className="form-group p-0">
                        <select
                          id="cities"
                          name="cities"
                          className={`form-control`}
                          onChange={(e) => {
                            const selectedId = e.target.value;

                            const updated = [...workingCities, selectedId];
                            setWorkingCities(updated);
                          }}
                          value=""
                        >
                          <option value="" disabled>
                            Select City
                          </option>
                          {STATES_OPTIONS.find(
                            (s) => s.state === values.address.state
                          )?.districts.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="w-100 mb-2">
                      <div className="d-flex flex-wrap gap-2 px-1">
                        {workingStates.map((d: any, dIx: number) => (
                          <span key={dIx} className="badge bg-primary p-2">
                            {d}
                            <Icon
                              icon="material-symbols:close-rounded"
                              className="cursor-pointer ms-1"
                              onClick={() => {
                                const updated = workingStates.filter(
                                  (_, i) => i !== dIx
                                );
                                setWorkingStates(updated);
                              }}
                            />
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="w-100 mb-3">
                      <div className="form-group p-0">
                        <select
                          id="states"
                          name="states"
                          className={`form-control`}
                          onChange={(e) => {
                            const selectedId = e.target.value;

                            const updated = [...workingStates, selectedId];
                            setWorkingStates(updated);
                          }}
                          value=""
                        >
                          <option value="" disabled>
                            Select State
                          </option>
                          {STATES_OPTIONS.map((item) => (
                            <option key={item.state} value={item.state}>
                              {item?.state}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between mt-4">
                      <button type="submit" className="btn btn-primary px-4">
                        Submit
                      </button>
                      <Link to="/employees">
                        <button className="btn btn-secondary px-4">
                          {t("cancel")}
                        </button>
                      </Link>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EmployeeForm;
